import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { SnackbarProvider } from 'notistack';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { theme } from './styles/theme';
import { Layout } from './components/layout/Layout';

// Page imports
import { Homepage } from './pages/Homepage';
import { LoginPage } from './pages/auth/LoginPage';
import { ActivateAccountPage } from './pages/auth/ActivateAccountPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { WorkflowsPage } from './pages/WorkflowsPage';
import { WorkflowEditorPage } from './pages/WorkflowEditorPage';
import { WorkflowCredentialsPage } from './pages/WorkflowCredentialsPage';
import { ExecutionsPage } from './pages/ExecutionsPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { ImportedNodesPage } from './pages/ImportedNodesPage';
import { ChatPage } from './pages/ChatPage';
import { NodeEditorPage } from './pages/NodeEditorPage';

// Admin pages
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { TenantManagement } from './pages/admin/TenantManagement';
import { UserManagement } from './pages/admin/UserManagement';
import { PermissionMatrix } from './pages/admin/PermissionMatrix';
import { SystemAnalytics } from './pages/admin/SystemAnalytics';
import { CredentialsConfig } from './pages/admin/CredentialsConfig';
import { SubscriptionManagement } from './pages/admin/SubscriptionManagement';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { RegistryPage } from './pages/RegistryPage';

import { NotFoundPage } from './pages/NotFoundPage';
import { LoadingSpinner } from './components/common/LoadingSpinner';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const hasPerm = (user: any, perm: string) => {
  if (!user) return false;
  if (user.role === 'system_admin') return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  return perms.includes(perm);
};

const hasAdminAccess = (user: any) => {
  if (!user) return false;
  if (user.role === 'system_admin') return true;
  return (
    hasPerm(user, 'org.manage') ||
    hasPerm(user, 'user.manage') ||
    hasPerm(user, 'credential.view')
  );
};

const AdminRoute: React.FC<{
  children: React.ReactNode;
  requireSystemAdmin?: boolean;
  requiredAnyPerms?: string[];
}> = ({ children, requireSystemAdmin, requiredAnyPerms }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireSystemAdmin && user.role !== 'system_admin') {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (requiredAnyPerms && requiredAnyPerms.length > 0) {
    const allowed = user.role === 'system_admin' || requiredAnyPerms.some((p) => hasPerm(user, p));
    if (!allowed) {
      return <Navigate to="/app/dashboard" replace />;
    }
  }

  if (!requireSystemAdmin && !hasAdminAccess(user)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
};

const AdminIndex: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner />
      </Box>
    );
  }

  if (user?.role === 'system_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (hasPerm(user, 'org.manage')) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (hasPerm(user, 'user.manage')) {
    return <Navigate to="/admin/users" replace />;
  }

  if (hasPerm(user, 'credential.view')) {
    return <Navigate to="/admin/credentials" replace />;
  }

  return <Navigate to="/app/dashboard" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/activate" element={<ActivateAccountPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      {/* Protected routes */}
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="workflows/new" element={<WorkflowEditorPage />} />
        <Route path="workflows/:id" element={<WorkflowEditorPage />} />
        <Route path="workflows/:id/credentials" element={<WorkflowCredentialsPage />} />
        <Route path="executions" element={<ExecutionsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="nodes/imported" element={<ImportedNodesPage />} />
        <Route path="node-editor" element={<NodeEditorPage />} />
        <Route path="node-editor/:sessionId" element={<NodeEditorPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="registry" element={<RegistryPage />} />
      </Route>

      {/* Admin routes */}
      <Route path="/admin" element={<AdminRoute><Layout /></AdminRoute>}>
        <Route index element={<AdminIndex />} />
        <Route path="dashboard" element={<AdminRoute requiredAnyPerms={['org.manage']}><AdminDashboard /></AdminRoute>} />
        <Route path="tenants" element={<AdminRoute requiredAnyPerms={['org.manage']}><TenantManagement /></AdminRoute>} />
        <Route path="users" element={<AdminRoute requiredAnyPerms={['org.manage', 'user.manage']}><UserManagement /></AdminRoute>} />
        <Route path="permissions" element={<AdminRoute requiredAnyPerms={['org.manage', 'user.manage']}><PermissionMatrix /></AdminRoute>} />
        <Route path="analytics" element={<AdminRoute requiredAnyPerms={['org.manage']}><SystemAnalytics /></AdminRoute>} />
        <Route path="credentials" element={<AdminRoute requiredAnyPerms={['credential.view']}><CredentialsConfig /></AdminRoute>} />
        <Route path="subscriptions" element={<AdminRoute requiredAnyPerms={['org.manage']}><SubscriptionManagement /></AdminRoute>} />
      </Route>

      {/* 404 page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <AuthProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;